/**
 * FavoriteActionModal — Modal avec 2 actions au clic sur un favori
 *
 * Propose :
 *  1. "Voir sur le site partenaire" → ouvre le browser avec tracking CJ/Sovrn
 *  2. "J'ai acheté" → ouvre PurchaseConfirmModal pour ajouter au dressing
 *
 * Spécifique Favoris : les autres écrans gardent la redirection directe.
 */
import React from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Image, Platform, StatusBar,
} from 'react-native';
import AppModal from './AppModal';
import { useTheme } from '../context/ThemeContext';

const PARTNER_NAMES = {
  zalando:  'Zalando',
  zara:     'Zara',
  iqueens:  'iQueens',
  mango:    'Mango',
  asos:     'ASOS',
  hm:       'H&M',
  cashmere: 'Cashmere Boutique',
  amazon:   'Amazon',
  farfetch: 'Farfetch',
  boohoo:   'Boohoo',
};

export default function FavoriteActionModal({
  product,
  onClose,
  onOpenSite,      // callback → ouvre le site partenaire (tracking)
  onMarkPurchased, // callback → déclenche confirmation d'achat
}) {
  const { dark } = useTheme();
  const s = React.useMemo(() => makeStyles(dark), [dark]);

  if (!product) return null;

  const partnerName = PARTNER_NAMES[product.partner] || product.partner || 'Partenaire';
  const topPad = Platform.OS === 'ios' ? 50 : (StatusBar.currentHeight || 0) + 10;

  return (
    <AppModal
      visible
      transparent
      animationType="slide"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <View style={s.overlay}>
        <View style={[s.sheet, { paddingTop: topPad }]}>

          {/* Header */}
          <View style={s.header}>
            <TouchableOpacity
              onPress={onClose}
              style={s.closeBtn}
              hitSlop={{ top:10, bottom:10, left:10, right:10 }}
            >
              <Text style={s.closeTxt}>✕</Text>
            </TouchableOpacity>
            <Text style={s.headerTitle}>Que voulez-vous faire ?</Text>
            <View style={{ width: 36 }} />
          </View>

          {/* Produit */}
          <View style={s.productRow}>
            <View style={s.productImg}>
              {product.image
                ? <Image source={{ uri: product.image }} style={s.productPhoto} resizeMode="cover" />
                : <Text style={s.productEmoji}>{product.emoji || '👗'}</Text>
              }
            </View>
            <View style={s.productInfo}>
              <Text style={s.productBrand}>
                {(product.brand || partnerName).toUpperCase()}
              </Text>
              <Text style={s.productName} numberOfLines={2}>
                {product.name || ''}
              </Text>
              <Text style={s.productPrice}>{product.price} €</Text>
            </View>
          </View>

          {/* Actions */}
          <View style={s.actionsWrap}>

            {/* Action 1 : Voir sur le site partenaire */}
            <TouchableOpacity
              style={[s.actionBtn, s.actionBtnPrimary]}
              onPress={onOpenSite}
              activeOpacity={0.85}
            >
              <View style={s.actionIconWrap}>
                <Text style={s.actionIcon}>🌐</Text>
              </View>
              <View style={s.actionTextWrap}>
                <Text style={s.actionTitle}>Voir sur {partnerName}</Text>
                <Text style={s.actionSub}>Ouvrir la page produit</Text>
              </View>
              <Text style={s.actionArrow}>→</Text>
            </TouchableOpacity>

            {/* Action 2 : J'ai acheté */}
            <TouchableOpacity
              style={[s.actionBtn, s.actionBtnSuccess]}
              onPress={onMarkPurchased}
              activeOpacity={0.85}
            >
              <View style={s.actionIconWrap}>
                <Text style={s.actionIcon}>✅</Text>
              </View>
              <View style={s.actionTextWrap}>
                <Text style={[s.actionTitle, s.actionTitleSuccess]}>J'ai acheté</Text>
                <Text style={[s.actionSub, s.actionSubSuccess]}>
                  Ajouter à ma garde-robe
                </Text>
              </View>
              <Text style={[s.actionArrow, s.actionArrowSuccess]}>→</Text>
            </TouchableOpacity>

          </View>

          {/* Info footer */}
          <Text style={s.infoFooter}>
            💰 Crédit MyTenue crédité automatiquement après validation de votre commande
          </Text>

        </View>
      </View>
    </AppModal>
  );
}

const makeStyles = (dark) => {
  const C = {
    bg:            dark ? '#1A1A1A' : '#FFFFFF',
    overlay:       'transparent',
    border:        dark ? '#333' : '#EEE',
    text:          dark ? '#EEE' : '#111',
    textMuted:     dark ? '#999' : '#666',
    textFaint:     dark ? '#666' : '#999',
    cardBg:        dark ? '#242424' : '#FAFAFA',
    closeBg:       dark ? '#2A2A2A' : '#F5F5F5',
    primaryBg:     dark ? '#1E3A5F' : '#EFF6FF',
    primaryBorder: dark ? '#2E4A6F' : '#BFDBFE',
    successBg:     dark ? '#14532D' : '#ECFDF5',
    successBorder: dark ? '#166534' : '#A7F3D0',
    successText:   dark ? '#86EFAC' : '#065F46',
  };

  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: C.overlay,
      justifyContent: 'flex-end',
    },
    sheet: {
      backgroundColor: C.bg,
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      paddingBottom: 24,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 20,
      paddingBottom: 14,
      borderBottomWidth: 1,
      borderBottomColor: C.border,
    },
    closeBtn: {
      width: 36, height: 36,
      borderRadius: 18,
      backgroundColor: C.closeBg,
      alignItems: 'center', justifyContent: 'center',
    },
    closeTxt: {
      fontSize: 14, fontWeight: '600',
      color: C.textMuted,
    },
    headerTitle: {
      fontSize: 15, fontWeight: '600',
      color: C.text,
    },
    productRow: {
      flexDirection: 'row',
      gap: 14,
      padding: 16,
      backgroundColor: C.cardBg,
      marginHorizontal: 20,
      marginTop: 16,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: C.border,
    },
    productImg: {
      width: 72, height: 88,
      borderRadius: 10,
      backgroundColor: dark ? '#2E2E2E' : '#F0F0F0',
      overflow: 'hidden',
      alignItems: 'center', justifyContent: 'center',
    },
    productPhoto: { width:'100%', height:'100%' },
    productEmoji: { fontSize: 32 },
    productInfo: { flex:1, justifyContent:'center' },
    productBrand: {
      fontSize: 10, letterSpacing: 1.6,
      color: C.textFaint,
      marginBottom: 4, fontWeight: '600',
    },
    productName: {
      fontSize: 14, fontWeight: '500',
      color: C.text,
      lineHeight: 19, marginBottom: 8,
    },
    productPrice: {
      fontSize: 18, fontWeight: '700',
      color: C.text,
    },

    actionsWrap: {
      padding: 20,
      gap: 12,
    },
    actionBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 14,
      padding: 16,
      borderRadius: 14,
      borderWidth: 1.5,
    },
    actionBtnPrimary: {
      backgroundColor: C.primaryBg,
      borderColor:     C.primaryBorder,
    },
    actionBtnSuccess: {
      backgroundColor: C.successBg,
      borderColor:     C.successBorder,
    },
    actionIconWrap: {
      width: 44, height: 44,
      borderRadius: 22,
      backgroundColor: C.bg,
      alignItems: 'center', justifyContent: 'center',
    },
    actionIcon: {
      fontSize: 22,
    },
    actionTextWrap: {
      flex: 1,
    },
    actionTitle: {
      fontSize: 15, fontWeight: '700',
      color: C.text,
      marginBottom: 2,
    },
    actionTitleSuccess: {
      color: C.successText,
    },
    actionSub: {
      fontSize: 12,
      color: C.textMuted,
    },
    actionSubSuccess: {
      color: C.successText,
      opacity: 0.85,
    },
    actionArrow: {
      fontSize: 22, fontWeight: '300',
      color: C.text,
    },
    actionArrowSuccess: {
      color: C.successText,
    },

    infoFooter: {
      fontSize: 11,
      color: C.textMuted,
      textAlign: 'center',
      paddingHorizontal: 30,
      paddingTop: 8,
      lineHeight: 16,
    },
  });
};
